import { filter, head, pathOr, pipe, propEq } from "ramda";
import { parsePhoneNumberWithError } from "libphonenumber-js";
import AgentPhoneIcon from "src/static/images/phone_icon.svg";
import AgentPhoneOutboundIcon from "src/static/images/phone_outbound_icon.svg";
import AgentChatIcon from "src/static/images/chat_icon.svg";
import AgentSmsIcon from "src/static/images/sms_icon.svg";
import {
  ADMIN,
  hiddenTitlePages,
  COLOR_VARIANTS,
  ASSISTANT_ENVIRONMENT,
  ASSISTANT_CHANNELS,
  REGIONS,
  CHANNEL_TO_DISPLAY_NAME,
  EMAIL_REGEX,
  ENVIRONMENTS,
  ENGAGEMENT_TYPES,
  TOOLTIP_DELAY,
  LANGUAGES
} from "@consts";
import { FIELD_TYPES } from "@hyro-components/fields/utils";
import { hasMarkdown } from "../helpers/markdown";

export { escapeCsvCell, sanitizeCsvRows, unescapeCsvCell } from "./csv";

const sortAlphabetically = array =>
  array.sort((str1, str2) => str1.localeCompare(str2));

const objectByAssistantId = (assistantId, assistants) =>
  pipe(filter(propEq("id", assistantId)), head)(assistants);

const wentLiveDate = (assistantId, assistants) =>
  pathOr(
    "2018-01-01 00:00",
    ["config", "analysis", "wentLiveDate"],
    objectByAssistantId(assistantId, assistants),
    "config.analysis.wentLiveDate"
  );

const isNumber = s => !isNaN(s) && s !== "";

const isEmail = s => EMAIL_REGEX.test(s);

const readTextFileAsync = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      resolve(reader.result);
    };
    reader.onerror = reject;
    reader.readAsText(file);
  });

const capitalizeString = str =>
  str.replace(/(^\w{1})|(\s+\w{1})/g, letter => letter.toUpperCase());

const capitalizeFirstLetter = (str) => str.charAt(0).toUpperCase() + str.slice(1);
const joinValuesWithPipe = (values) => values.join("|");

const replaceSpecialChars = c => str => str.toLowerCase().trimStart().replace(/[^A-Za-z0-9]/g, c);

const startsOrEndsWithWhitespace = str => /^\s|\s$/.test(str);

const isValidCssColor = value =>
  CSS.supports("background", value) && !startsOrEndsWithWhitespace(value);

const checkValidCssValueOrBlank = value =>
  isValidCssColor(value) || value === ""
    ? ""
    : "Invalid color input, please enter a valid hex code, RGB value or color name.";

const isValidHexColor = value => {
  const hexRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
  return hexRegex.test(value);
};

const isValidColorName = value => {
  const s = new Option().style;
  s.color = value;
  return s.color !== "" && !value.includes("#") && !value.includes("(");
};

const checkValidColor = value =>
  isValidHexColor(value) || isValidColorName(value) || value === ""
    ? ""
    : "Invalid color input, please enter a valid hex code (e.g., #FF5733) or color name (e.g., red).";

const eventCustomStyleByColorVariant = (colorVariant) => {
  const backgroundColor = ({ palette }) =>
    colorVariant ? palette[colorVariant].main : palette.grey[300];
  return {
    backgroundColor,
    "&:hover": {
      backgroundColor
    },
    "color": theme =>
      theme.palette.common[
        !colorVariant || colorVariant === COLOR_VARIANTS.WARNING
          ? "black"
          : "white"
      ]
  };
};

const assistantComparator = (a, b) => {
  const environmentOrder = [ASSISTANT_ENVIRONMENT.PRODUCTION, ASSISTANT_ENVIRONMENT.STAGING, ASSISTANT_ENVIRONMENT.DEMO, ASSISTANT_ENVIRONMENT.TEST, ASSISTANT_ENVIRONMENT.DISABLED];

  const aChannel = CHANNEL_TO_DISPLAY_NAME[a.assistantChannel];
  const bChannel = CHANNEL_TO_DISPLAY_NAME[b.assistantChannel];

  // Step 1: Compare by channel (Voice items come before Chat)
  if (aChannel === CHANNEL_TO_DISPLAY_NAME.Phone && bChannel !== CHANNEL_TO_DISPLAY_NAME.Phone) return -1;
  if (aChannel !== CHANNEL_TO_DISPLAY_NAME.Phone && bChannel === CHANNEL_TO_DISPLAY_NAME.Phone) return 1;

  if (aChannel === CHANNEL_TO_DISPLAY_NAME.Web && bChannel !== CHANNEL_TO_DISPLAY_NAME.Web) return -1;
  if (aChannel !== CHANNEL_TO_DISPLAY_NAME.Web && bChannel === CHANNEL_TO_DISPLAY_NAME.Web) return 1;

  // Step 2: If both are Voice, sort by engagementType (OUTBOUND last)
  if (aChannel === CHANNEL_TO_DISPLAY_NAME.Phone && bChannel === CHANNEL_TO_DISPLAY_NAME.Phone) {
    if (a.engagementType === ENGAGEMENT_TYPES.OUTBOUND && b.engagementType !== ENGAGEMENT_TYPES.OUTBOUND) return 1;
    if (a.engagementType !== ENGAGEMENT_TYPES.OUTBOUND && b.engagementType === ENGAGEMENT_TYPES.OUTBOUND) return -1;
  }

  // Step 3: If channels are the same, compare by environment
  const environmentComparison = (environmentOrder.indexOf(a.environment) + 1 || environmentOrder.length + 1) - (environmentOrder.indexOf(b.environment) + 1 || environmentOrder.length + 1);
  if (environmentComparison !== 0) return environmentComparison;

  // Step 4: If environment is also the same, compare alphabetically by name
  return a.assistantName.localeCompare(b.assistantName);
};

export const isWebAssistant = (channel) => channel === ASSISTANT_CHANNELS.WEB || channel === CHANNEL_TO_DISPLAY_NAME.Web;

export const isPhoneAssistant = (channel) => channel === ASSISTANT_CHANNELS.PHONE || channel === CHANNEL_TO_DISPLAY_NAME.Phone;

export const isSmsAssistant = (channel) => channel === ASSISTANT_CHANNELS.SMS;

export const getAgentIcon = ({ channel, engagementType }) => {
  if (isWebAssistant(channel)) return AgentChatIcon;
  if (isPhoneAssistant(channel) && engagementType === ENGAGEMENT_TYPES.OUTBOUND) return AgentPhoneOutboundIcon;
  if (isPhoneAssistant(channel)) return AgentPhoneIcon;
  if (isSmsAssistant(channel)) return AgentSmsIcon;

  return null;
};

const doesTextContainTags = (text) => /<[^>]*>/.test(text);

const isValidSSML = (text) => {
  // First check with the existing regex
  const ssmlRegex = /<speak>.*?<\/speak>|<(break|say-as|prosody|phoneme|s|p|w|emphasis|sub|mark|voice|amazon:effect)[^>]*>|^[^<>]*$/;
  if (!ssmlRegex.test(text)) {
    return false;
  }

  // Additional validation for unclosed tags
  const selfClosingTags = ["break", "mark"];
  for (const tag of selfClosingTags) {
    const unclosedTagRegex = new RegExp(`<${tag}[^>]*[^/]>`, "g");
    if (unclosedTagRegex.test(text)) {
      return false;
    }
  }

  return true;
};

const deleteWidgetCookie = (cookieName) => {
  document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

export const removeUnderscore = str => str?.replace(/_/g, " ");

const capitalizeAndCleanUnderscore = (str) => {
  const isString = typeof str === "string" || str instanceof String;
  return isString ? capitalize(removeUnderscore(str)) : str;
};

const toLowerCaseAndCapitalize = (str) => {
  const isString = typeof str === "string" || str instanceof String;
  return isString ? capitalize(str.toLowerCase()) : str;
};
export const capitalize = (str = "") =>
  str.charAt(0).toUpperCase() + str.slice(1);

const TITLE_CASE_MINOR_WORDS = new Set([
  "a", "an", "the", "and", "or", "but", "nor", "as",
  "at", "by", "for", "in", "of", "on", "to", "with"
]);

export const toTitleCase = (str = "") => {
  if (!str) return "";
  return str
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .toLowerCase()
    .trim()
    .split(/\s+/)
    .map((word, index) => (index > 0 && TITLE_CASE_MINOR_WORDS.has(word)
      ? word
      : word.charAt(0).toUpperCase() + word.slice(1)))
    .join(" ");
};

// Taken from https://stackoverflow.com/a/831583
const middleEllipsisTrim = (string, maxLength) => {
  if (!string || maxLength < 1 || string.length <= maxLength) return string;
  if (maxLength === 1) return string.substring(0, 1) + "...";

  const midpoint = Math.ceil(string.length / 2);
  const toRemove = string.length - maxLength;
  const leftStrip = Math.ceil(toRemove / 2);
  const rightStrip = toRemove - leftStrip;
  return (
    string.substring(0, midpoint - leftStrip)
    + "..."
    + string.substring(midpoint + rightStrip)
  );
};

const extractNameFromEmail = email =>
  capitalize(email?.split("@")?.[0] || "");

const checkWhitespace = s => (/\s/).test(s);

const stringToColor = (value) => {
  value = value || " ";
  const defaultColors = [
    "#A62A21",
    "#7e3794",
    "#0B51C1",
    "#3A6024",
    "#A81563",
    "#B3003C"
  ];
  const charCodes = [...value].map(letter => letter.charCodeAt(0));
  const len = charCodes.length;

  const a = (len % (defaultColors.length - 1)) + 1;
  const c = charCodes.reduce((current, next) => current + next) % defaultColors.length;

  let random = charCodes[0] % defaultColors.length;
  for (let i = 0; i < len; i++)
    random = ((a * random) + c) % defaultColors.length;
  return defaultColors[random];
};

const getStringInitials = (string = " ", maxInitials) => string?.split(/\s/)
  .map(part => part.substring(0, 1).toUpperCase())
  .filter(v => !!v)
  .slice(0, maxInitials)
  .join("")
  .toUpperCase();

const NANP_CALLING_CODE = "1";

const validatePhoneNumber = (input, region = REGIONS.US) => {
  try {
    const formattedInput = formatPhoneNumberForRegion(input, region);
    const number = parsePhoneNumberWithError(formattedInput, region);
    // Puerto Rico (787/939) and other NANP territories share the US +1 calling code
    // but resolve to their own ISO country, so match the whole NANP for the US region.
    const matchesRegion = region === REGIONS.US
      ? number.countryCallingCode === NANP_CALLING_CODE
      : number.country === region;
    const isValidPhoneNumber = number.isValid() && matchesRegion;
    const formattedNumber = number.format("E.164");

    return isValidPhoneNumber && (formattedNumber === formattedInput);
  } catch (e) {
    return false;
  }
};

export const cleanPhoneNumber = (input) => input.replace(/[\s()-]/g, "");

export const formatPhoneNumberForRegion = (input, region = REGIONS.US) => {
  const trimmedValue = cleanPhoneNumber(input);

  if (region === REGIONS.US) {
    return trimmedValue.startsWith("+1") ? trimmedValue : `+1${trimmedValue}`;
  }

  return trimmedValue;
};

export const formatPhoneNumber = (value) => {
  const trimmedValue = cleanPhoneNumber(value);
  return !trimmedValue.includes("+") ? `+${trimmedValue}` : trimmedValue;
};

const validatePhoneField = (input) => {
  // This function is for validating phone field type.
  // This is needed because we are not always getting a E164 format, and we need to somehow validate the phone numbers.
  // It should have digits only and between 4 to 11 digits.
  try {
    const formattedInput = formatPhoneNumber(input);
    const validNumberRegex = /^(\+)\d{4,11}$/;

    return validNumberRegex.test(formattedInput);
  } catch (e) {
    return false;
  }
};

export const filterObjectByKeys = (obj, keys) => Object.keys(obj)
  .filter(key => keys.includes(key))
  .reduce((acc, key) => {
    acc[key] = obj[key];
    return acc;
  }, {});

// Single shared canvas context — creating a canvas per call leaks GPU-backed
// memory fast enough to crash the tab when measuring thousands of cells.
let textMeasureContext = null;

export const getTextWidth = ({ text, extraWidth = 0, font = "400 12px Inter, sans-serif" }) => {
  if (!textMeasureContext) {
    textMeasureContext = document.createElement("canvas").getContext("2d");
  }

  textMeasureContext.font = font;

  const metrics = textMeasureContext.measureText(text);
  return metrics.width + extraWidth;
};
export const filterCollectionsByEnv = (collections) => collections?.filter(({ expose_to_external_users }) =>
  import.meta.env.VITE_ENV !== "production" || expose_to_external_users);
export const commaNotation = (number) => number?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

export const formatPercent = (value, total) => {
  if (!total) return "0%";
  const pct = (value / total) * 100;
  if (pct >= 100) return "100%";
  if (pct < 10) return `${pct.toFixed(1)}%`.replace(/\.0%$/, "%");
  return `${Math.round(pct)}%`;
};

/**
 * @param {(string|string[])} input - input to check
 * @param {string} channel - channel to check
 */
export const checkAssistantChannel = (input, channel) => {
  if (Array.isArray(input)) {
    return input.map((str) => str.toLowerCase()).includes(channel.toLowerCase());
  }
  return input?.toLowerCase() === channel.toLowerCase();

};

export const shouldReIdentify = (ldContext, user) => {
  const [, accountId, assistantId] = location?.pathname?.split("/") || [];

  const isAdmin = accountId === ADMIN;
  const hasAccountOnly = !assistantId;

  const isDifferentAccount = ldContext?.custom?.accountId !== accountId;
  const isDifferentAssistant = ldContext?.custom?.assistantId !== assistantId;

  return isAdmin
    ? ldContext?.custom?.userId !== user.sub
    : hasAccountOnly
      ? isDifferentAccount
      : isDifferentAccount || isDifferentAssistant;
};

// No skills No new Overview
export const isNotCodefullAgent = (agentId) => !(import.meta.env.VITE_NON_CODELESS_AGENTS.split(",").includes(agentId));
// No Overview
const isNotPartialCodelessAgent = (agentId) => !(import.meta.env.VITE_PARTIAL_CODELESS_AGENTS.split(",").includes(agentId));

export const isFullyCodelessAgent = (agentId) => isNotCodefullAgent(agentId) && isNotPartialCodelessAgent(agentId);

export const isBoolean = (value) => typeof value === "boolean";

export const isProd = () => import.meta.env.VITE_ENV === ENVIRONMENTS.PRODUCTION;

export const areStringsEqual = (str1, str2) => {
  if (!str1 || !str2) return false;
  return str1.toLowerCase().trim() === str2.toLowerCase().trim();
};

export const hasActiveFilters = (filters) => filters.some(filter => filter?.value?.length > 0);

export const getLinkToAgent = (accountId, assistantId) => (accountId && assistantId
  ? `${window.location.protocol}//${window.location.host}/${accountId}/${assistantId}`
  : undefined);

/**
 * Triggers a file download from a URL
 * @param {string} url - The URL of the file to download
 * @param {string} [filename='download'] - Optional filename for the downloaded file
 */
export const downloadFile = (url, filename) => {
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const downloadJsonFile = (data, filename) => {
  try {
    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    downloadFile(url, filename);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Error downloading JSON file:", error);
    throw error;
  }
};

const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? `rgb(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)})`
    : null;
};

const isMultiCollectionRef = (field) => field?.options?.length === 1 && field?.options[0]?.type === FIELD_TYPES.COLLECTION_REF;

const isHyroUser = (user) => user?.email?.endsWith("@hyro.ai");

/**
 * Validates if a string is a valid MongoDB ObjectId (24 hex characters)
 * @param {string} value - The value to validate
 * @returns {boolean} - True if valid ObjectId, false otherwise
 */
const isValidObjectId = (value) => {
  if (!value || typeof value !== "string") return false;
  return /^[0-9a-fA-F]{24}$/.test(value.trim());
};

// The punctuation both language checks allow, as a character-class body so callers that need the
// same set with a small delta (saved filter names add the straight apostrophe) can derive their
// regex from it instead of re-transcribing it and drifting.
// The leading "-" must stay first: anywhere else it reads as a range and throws at construction.
export const ENGLISH_PUNCTUATION_CHAR_CLASS = "-.,\u2019!?{}_[\\]@#$%&()-+=/ \"\u201C\u201D:";

const SPANISH_ACCENT_CHARS = "\u00E1\u00E9\u00ED\u00F3\u00FA\u00C1\u00C9\u00CD\u00D3\u00DA\u00FC\u00DC\u00F1\u00D1\u00BF\u00A1";

const ENGLISH_CHAR_REGEX = new RegExp(`[^a-zA-Z0-9${ENGLISH_PUNCTUATION_CHAR_CLASS}]`);
// Accents sit after the class's leading "-", matching the original ordering.
const SPANISH_CHAR_REGEX = new RegExp(
  `[^a-zA-Z0-9-${SPANISH_ACCENT_CHARS}${ENGLISH_PUNCTUATION_CHAR_CLASS.slice(1)}]`
);

const hasEmail = text => EMAIL_REGEX.test(text);
const hasUrl = text =>
  /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&/=]*)/.test(
    text
  );
const hasNoSpaceColon = text => !/[A-Za-z]:[A-Za-z]/.test(text);

const containsValidLanguageCharacters = (str, language) => {
  // If no language is provided, check if valid in either English or Spanish
  if (!language) {
    const isValidEnglish = !ENGLISH_CHAR_REGEX.test(str);
    const isValidSpanish = !SPANISH_CHAR_REGEX.test(str);
    return isValidEnglish || isValidSpanish;
  }

  if (language === LANGUAGES.ENGLISH) {
    return !ENGLISH_CHAR_REGEX.test(str);
  }

  if (language === LANGUAGES.SPANISH) {
    return !SPANISH_CHAR_REGEX.test(str);
  }

  return true;
};

const rowResponseInvalidFormatMessages = (input, language = null) => {
  const trimmedInput = (input ?? "").trim().replace(/^-+|-+$/g, "");

  if (hasEmail(trimmedInput) || hasUrl(trimmedInput) || hasMarkdown(trimmedInput)) {
    return [];
  }

  const errorMessages = [];

  if (!hasNoSpaceColon(trimmedInput)) {
    errorMessages.push("Input must not contain a colon without spaces.");
  }

  if (trimmedInput && !containsValidLanguageCharacters(trimmedInput, language)) {
    if (language) {
      errorMessages.push(`Input must contain only valid ${toLowerCaseAndCapitalize(language)} characters.`);
    } else {
      errorMessages.push("Input must contain only valid English or Spanish characters.");
    }
  }

  return errorMessages;
};

const createSampleForCsv = columnSpec =>
  columnSpec.reduce(
    (acc, { label, columnType }) => ({
      ...acc,
      [label]: getSampleData(columnType)
    }),
    {}
  );

const getSampleData = (columnType) => {
  if (columnType === FIELD_TYPES.MULTIPLE_VALUES) {
    return [];
  }
  return "";
};

const camelCaseToWords = (str) => str.replace(/([A-Z])/g, " $1").toLowerCase().trim();

// Accepts either string[] (e.g. ["ENGLISH"]) or {language: string}[] (e.g. [{language: "ENGLISH"}]).
// Returns an upper-cased string[] with falsy entries dropped.
const normalizeLanguageList = (input) => {
  if (!Array.isArray(input)) return [];
  return input
    .map(item => (typeof item === "string" ? item : item?.language))
    .filter(Boolean)
    .map(lang => lang.toUpperCase());
};

// Builds the { LANG: voiceName } map the speech endpoints expect.
// Falls back to a single-entry map keyed by `fallbackLanguage` when no languages are provided.
const buildVoiceNamesMap = (languages, voiceName, fallbackLanguage) => {
  const list = normalizeLanguageList(languages);
  const langs = list.length ? list : [fallbackLanguage].filter(Boolean).map(l => l.toUpperCase());
  return Object.fromEntries(langs.map(lang => [lang, voiceName]));
};

export {
  buildVoiceNamesMap,
  camelCaseToWords,
  normalizeLanguageList,
  createSampleForCsv,
  rowResponseInvalidFormatMessages,
  isMultiCollectionRef,
  joinValuesWithPipe,
  extractNameFromEmail,
  middleEllipsisTrim,
  assistantComparator,
  checkValidCssValueOrBlank,
  checkValidColor,
  deleteWidgetCookie,
  doesTextContainTags,
  isValidSSML,
  eventCustomStyleByColorVariant,
  objectByAssistantId,
  isNumber,
  isEmail,
  wentLiveDate,
  sortAlphabetically,
  readTextFileAsync,
  capitalizeString,
  capitalizeFirstLetter,
  replaceSpecialChars,
  capitalizeAndCleanUnderscore,
  toLowerCaseAndCapitalize,
  hiddenTitlePages,
  checkWhitespace,
  stringToColor,
  getStringInitials,
  validatePhoneNumber,
  validatePhoneField,
  TOOLTIP_DELAY,
  hexToRgb,
  isHyroUser,
  isValidObjectId
};

/**
 * Standard number of digits displayed after the decimal point across all dashboards.
 */
export const DECIMAL_PRECISION = 1;

/**
 * Rounds a number half-up at the given precision.
 * `Number.prototype.toFixed` rounds on the binary representation, so values such as
 * 1.005 round down; scaling before `Math.round` keeps the rounding half-up.
 */
const roundHalfUp = (value, digits) => {
  const factor = 10 ** digits;
  const scaled = value * factor;
  // Re-scale through a fixed representation to drop binary noise (e.g. 100.49999999999999).
  const corrected = Number(scaled.toPrecision(15));
  return (Math.sign(corrected) || 1) * Math.round(Math.abs(corrected)) / factor;
};

/**
 * Formats a decimal metric for display, rounded half-up to `digits` places.
 * Whole numbers are rendered without a trailing zero (1.04 -> "1", 4.56 -> "4.6").
 *
 * @param {number|null|undefined} value - the value to format
 * @param {number} [digits=DECIMAL_PRECISION] - digits after the decimal point
 * @returns {string|null} the formatted value, or null when there is nothing to show
 */
export const formatDecimal = (value, digits = DECIMAL_PRECISION) => {
  if (value == null || Number.isNaN(Number(value))) return null;
  const rounded = roundHalfUp(Number(value), digits);
  return Number.isFinite(rounded) ? rounded.toString() : null;
};

/**
 * Formats a percentage metric for display, using the shared decimal precision.
 *
 * @param {number|null|undefined} value - the percentage value (0-100, not a ratio)
 * @param {number} [digits=DECIMAL_PRECISION] - digits after the decimal point
 * @returns {string|null} the formatted percentage including the sign, or null
 */
export const formatPercentValue = (value, digits = DECIMAL_PRECISION) => {
  const formatted = formatDecimal(value, digits);
  return formatted == null ? null : `${formatted}%`;
};

export const formatCountWithShare = (count, percentage) => `${commaNotation(count ?? 0)} (${formatPercentValue(percentage ?? 0)})`;

export const formatCount = (count, singular, plural) => {
  if (count == null || Number.isNaN(Number(count))) return null;
  const rounded = Math.round(Number(count));
  return `${rounded.toLocaleString()} ${rounded === 1 ? singular : plural}`;
};

export const formatCallCount = (count) => formatCount(count, "call", "calls");
